import React, { useEffect, useRef, useState } from 'react';
import { ClipboardCheck, RefreshCw } from 'lucide-react';
import { commandId } from '../components/CkOperationsShared';

export default function Arrival({ po, api, active, canOperate }) {
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState('');
  const [driver, setDriver] = useState('');
  const [temperature, setTemperature] = useState('');
  const alive = useRef(false);
  const lock = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const current = () => alive.current && active();

  async function open(event) {
    event.preventDefault();
    if (!current() || lock.current || !canOperate) return;
    if (!pending && temperature.trim() && !/^-?\d+(\.\d+)?$/.test(temperature.trim())) {
      setError('Enter a decimal temperature or leave it blank.'); return;
    }
    let body;
    try {
      body = pending || { command_id: commandId(), po_id: po.id,
        delivery_note_ref: note.trim() || null, driver_name: driver.trim() || null,
        vehicle_temp_c: temperature.trim() || null };
    } catch { setError('This browser cannot create a secure arrival reference. Nothing was sent.'); return; }
    lock.current = true; setBusy(true); setError(''); setPending(body);
    try {
      const result = await api.open(body);
      if (current()) { setReceipt(result); setPending(null); }
    } catch (e) {
      if (!current()) return;
      if ([400, 422].includes(e.status)) { setPending(null); setError(e.message); }
      else if (e.status === 409) setError(`${e.message} Refresh deliveries to check the existing receipt.`);
      else setError(`${e.message} Arrival is not confirmed here. Retry the same arrival or refresh deliveries to check its status.`);
    } finally { lock.current = false; if (current()) setBusy(false); }
  }

  async function view() {
    if (!current() || lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const result = await api.receipt(po.active_receipt_id);
      if (current()) setReceipt(result);
    } catch (e) { if (current()) setError(e.message); }
    finally { lock.current = false; if (current()) setBusy(false); }
  }

  return <section className="receiving-arrival" aria-label={`Arrival ${po.po_number || po.id}`}>
    {error && <p role="alert" className="receiving-error">{error}</p>}
    {receipt ? <div role="status">
      <h2>Receipt {receipt.status === 'open' ? 'opened' : receipt.status.replaceAll('_', ' ')}</h2>
      <dl><dt>Receipt reference</dt><dd>{receipt.id}</dd><dt>Arrival recorded</dt><dd>{receipt.arrived_at}</dd>
        <dt>Delivery note</dt><dd>{receipt.delivery_note_ref || 'Not recorded'}</dd>
        <dt>Driver</dt><dd>{receipt.driver_name || 'Not recorded'}</dd>
        <dt>Vehicle temperature C</dt><dd>{receipt.vehicle_temp_c ?? 'Not measured'}</dd></dl>
      <a href={`/m/receipt/${encodeURIComponent(receipt.id)}`}>Open receipt lines</a>
    </div> : po.active_receipt_id ? <button type="button" disabled={busy} onClick={view}><ClipboardCheck size={18} />{busy ? 'Loading receipt...' : 'View existing receipt'}</button>
      : canOperate ? <form onSubmit={open} aria-label={`Check in ${po.po_number || po.id}`}>
        <h2>Delivery arrival</h2>
        <fieldset disabled={busy || !!pending}>
          <label>Delivery note reference<input value={note} maxLength={200} onChange={e => setNote(e.target.value)} /></label>
          <label>Driver name<input value={driver} maxLength={200} onChange={e => setDriver(e.target.value)} /></label>
          <label>Vehicle temperature C<input inputMode="decimal" value={temperature} onChange={e => setTemperature(e.target.value)} /></label>
        </fieldset>
        <button type="submit" disabled={busy}>{pending ? <RefreshCw size={18} /> : <ClipboardCheck size={18} />}{busy ? 'Recording arrival...' : pending ? 'Retry same arrival' : 'Record arrival'}</button>
      </form> : <p>Read-only access</p>}
  </section>;
}
